import React, { useEffect, useState } from 'react';
import { X, ExternalLink, Palette, Check, Loader2, Maximize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * ThemePreviewModal - Full-screen preview of a WordPress theme
 * Fetches and displays the full-length theme screenshot from theme homepage
 */
const ThemePreviewModal = ({ 
  theme, 
  isOpen, 
  onClose, 
  onSelect,
  isSelected 
}) => {
  const [fullPreviewUrl, setFullPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewSource, setPreviewSource] = useState('thumbnail');
  const [demoUrl, setDemoUrl] = useState(null);
  
  // Fetch full preview when modal opens
  useEffect(() => {
    if (!isOpen || !theme?.theme_id) {
      setFullPreviewUrl(null);
      setLoading(false);
      return;
    }
    
    const fetchFullPreview = async () => {
      setLoading(true);
      
      // OPTIMIZATION: Use thumbnail directly for all themes
      // The full-preview endpoint makes multiple slow HTTP calls to external sites (10-20+ seconds)
      // The thumbnail is already high quality and available immediately
      setFullPreviewUrl(theme.thumbnail);
      setPreviewSource(theme.is_wordpress_org ? 'wordpress_org' : 'local');
      setDemoUrl(theme.preview_url || theme.homepage || null);
      setLoading(false);
    };
    
    fetchFullPreview();
  }, [isOpen, theme?.theme_id, theme?.thumbnail, theme?.is_wordpress_org, theme?.preview_url, theme?.homepage]);
  
  // Handle ESC key - must be before early return
  useEffect(() => {
    if (!isOpen) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !theme) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  
  // Determine which image to display
  const displayImageUrl = fullPreviewUrl || theme.thumbnail;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={handleBackdropClick}
          data-testid="theme-preview-modal"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: 'spring', damping: 25 }}
            className="bg-white rounded-2xl overflow-hidden shadow-2xl w-full max-w-6xl max-h-[95vh] flex flex-col"
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between bg-zinc-50 flex-shrink-0">
              <div className="flex items-center gap-4">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                </div>
                <div>
                  <h2 className="font-bold text-lg text-zinc-900" style={{ fontFamily: 'Outfit' }}>
                    {theme.name}
                  </h2>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Primary category */}
                    <span className="text-sm text-zinc-500">{theme.category}</span>
                    {/* All category tags */}
                    {theme.category_tags && theme.category_tags.length > 1 && (
                      <>
                        <span className="text-zinc-300">|</span>
                        <div className="flex flex-wrap gap-1">
                          {theme.category_tags
                            .filter(tag => tag !== theme.category)
                            .map((tag, idx) => (
                              <span 
                                key={idx}
                                className="px-2 py-0.5 text-xs bg-zinc-100 text-zinc-600 rounded-full"
                              >
                                {tag}
                              </span>
                            ))}
                        </div>
                      </>
                    )}
                    {previewSource === 'theme_homepage' && (
                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                        Full Preview
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* Color Scheme */}
                {theme.color_scheme && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-zinc-200">
                    <Palette className="w-4 h-4 text-zinc-400" />
                    <div className="flex gap-1.5">
                      <div 
                        className="w-5 h-5 rounded-full border border-zinc-200"
                        style={{ backgroundColor: theme.color_scheme.primary }}
                        title="Primary"
                      />
                      <div 
                        className="w-5 h-5 rounded-full border border-zinc-200"
                        style={{ backgroundColor: theme.color_scheme.accent }}
                        title="Accent"
                      />
                      <div 
                        className="w-5 h-5 rounded-full border border-zinc-200"
                        style={{ backgroundColor: theme.color_scheme.secondary }}
                        title="Secondary"
                      />
                    </div>
                  </div>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="hover:bg-zinc-200"
                  data-testid="theme-preview-close-btn"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Theme Screenshot - Scrollable with lower z-index so it goes behind footer */}
            <div className="flex-1 overflow-y-auto bg-zinc-800 relative z-10">
              <div className="flex flex-col items-center p-4 min-h-full w-full">
                {loading ? (
                  <div className="flex flex-col items-center justify-center h-96 gap-4">
                    <Loader2 className="w-10 h-10 text-amber-400 animate-spin" />
                    <p className="text-zinc-400">Loading full theme preview...</p>
                  </div>
                ) : (
                  <img
                    src={displayImageUrl}
                    alt={theme.name}
                    className="shadow-2xl rounded-lg w-full"
                    style={{ 
                      maxWidth: '100%',
                      width: '100%',
                      maxHeight: previewSource === 'thumbnail' ? 'calc(95vh - 200px)' : 'none',
                      objectFit: 'contain'
                    }}
                    onError={(e) => {
                      e.target.src = theme.thumbnail || 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200';
                    }}
                  />
                )}
              </div>
            </div>

            {/* Modal Footer - Fixed at bottom with high z-index */}
            <div className="px-6 py-4 border-t border-zinc-200 bg-white flex flex-col gap-4 flex-shrink-0 relative z-20">
              {/* Full description */}
              <div className="max-w-full overflow-hidden">
                <p className="text-sm text-zinc-600 leading-relaxed line-clamp-2">
                  {theme.description || 'A beautiful WordPress theme for your business.'}
                </p>
                {/* Category Tags - Scrollable horizontally if too many */}
                {theme.category_tags && theme.category_tags.length > 0 && (
                  <div className="flex items-center gap-2 mt-3 overflow-x-auto pb-1 scrollbar-hide">
                    <span className="text-xs text-zinc-400 flex-shrink-0">Categories:</span>
                    {theme.category_tags.map((tag, idx) => (
                      <span 
                        key={idx}
                        className={`px-2 py-1 text-xs rounded-full flex-shrink-0 ${
                          tag === theme.category 
                            ? 'bg-amber-100 text-amber-700 font-medium' 
                            : 'bg-zinc-100 text-zinc-600'
                        }`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end gap-3">
                {/* Live Demo button - only shown if a valid demo URL exists */}
                {demoUrl && (
                  <Button
                    variant="outline"
                    onClick={() => window.open(demoUrl, '_blank')}
                    className="text-zinc-600"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Live Demo
                  </Button>
                )}
                {fullPreviewUrl && fullPreviewUrl !== theme.thumbnail && (
                  <Button
                    variant="outline"
                    onClick={() => window.open(fullPreviewUrl, '_blank')}
                    className="text-zinc-600"
                  >
                    <Maximize2 className="w-4 h-4 mr-2" />
                    Full Size
                  </Button>
                )}
                <Button
                  onClick={() => {
                    onSelect(theme.theme_id);
                    onClose();
                  }}
                  className={`px-6 ${
                    isSelected 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-amber-500 hover:bg-amber-600'
                  } text-white`}
                  data-testid="theme-preview-select-btn"
                >
                  {isSelected ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Selected
                    </>
                  ) : (
                    'Select This Theme'
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ThemePreviewModal;
